import React, { useEffect, useState } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Seo from '../SEO/Seo';
import { fetchTales } from '../../api';

const APP_STORE_URL = 'https://apps.apple.com/app/rejection-tales/id6813563793';

const C = {
  primary: '#4A3428',
  primaryDark: '#3A281F',
  secondary: '#8B6F5C',
  bg: '#F0EFED',
  bgAlt: '#F7F6F4',
  surface: '#FFFFFF',
  text: '#2A221C',
  muted: '#6E635A',
  border: '#DDD6CF',
};

/** Official-style App Store download badge */
const AppStoreBadge = ({ tone = 'dark' }) => {
  const onLight = tone === 'light';
  return (
    <Box
      component="a"
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download Rejection Tales on the App Store"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.25,
        textDecoration: 'none',
        color: onLight ? '#fff' : C.primary,
        bgcolor: onLight ? '#1C1C1E' : '#F7F6F4',
        border: onLight ? '1px solid rgba(255,255,255,0.12)' : `1px solid ${C.border}`,
        borderRadius: '12px',
        px: 2.25,
        py: 1.15,
        minHeight: 52,
        width: 'fit-content',
        maxWidth: '100%',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
        boxShadow: onLight ? '0 8px 24px rgba(0,0,0,0.18)' : 'none',
        '&:hover': {
          transform: 'translateY(-2px)',
          bgcolor: onLight ? '#000' : '#fff',
          boxShadow: onLight
            ? '0 12px 28px rgba(0,0,0,0.28)'
            : '0 8px 20px rgba(42,34,28,0.10)',
        },
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 24 24"
        aria-hidden
        sx={{ width: 28, height: 28, flexShrink: 0, fill: 'currentColor' }}
      >
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.22-1.98 1.08-3.13-1.05.04-2.31.7-3.06 1.58-.67.78-1.25 2.02-1.1 3.21 1.16.09 2.35-.63 3.08-1.66" />
      </Box>
      <Box sx={{ textAlign: 'left', lineHeight: 1.15 }}>
        <Box
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.68rem',
            letterSpacing: '0.02em',
            opacity: 0.85,
            fontFamily: '"DM Sans", sans-serif',
          }}
        >
          Download on the
        </Box>
        <Box
          component="span"
          sx={{
            display: 'block',
            fontSize: '1.2rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            fontFamily: '"DM Sans", sans-serif',
          }}
        >
          App Store
        </Box>
      </Box>
    </Box>
  );
};

const PILLARS = [
  {
    title: 'Stories',
    body: 'Real nos from jobs, school, startups, and personal life. Feel less alone in minutes.',
  },
  {
    title: 'Care',
    body: 'A soft landing when it still hurts. Brighten, check in, and move at your pace.',
  },
  {
    title: 'Luna',
    body: 'Someone to sit with you. Listen first. Coach only when you ask.',
  },
  {
    title: 'Practice',
    body: 'Short drills for interviews, resilience, and talking about rejection with dignity.',
  },
];

const PATH = [
  { n: '01', title: 'Land', body: 'Name what happened without performing a win.' },
  { n: '02', title: 'Feel', body: 'Sit with it, or talk it through with Luna.' },
  { n: '03', title: 'Share', body: 'Put the weight into words when you’re ready.' },
  { n: '04', title: 'Practice', body: 'Rebuild the skill that mattered, one card at a time.' },
];

const FALLBACK_STORIES = [
  {
    _id: 'fallback-1',
    title: 'We loved your energy, but we’ve decided to move forward with someone who has more experience.',
    groupName: 'Profession',
  },
  {
    _id: 'fallback-2',
    title: 'I almost got hired. Then they Googled me.',
    groupName: 'Profession',
  },
  {
    _id: 'fallback-3',
    title: '3M Young Scientists Challenge Rejection',
    groupName: 'Personal',
  },
];

const display = {
  fontFamily: '"Literata", Georgia, serif',
  letterSpacing: '-0.022em',
  color: C.text,
};
const sans = { fontFamily: '"DM Sans", system-ui, sans-serif' };

const Landing = () => {
  const { pathname } = useLocation();
  const [stories, setStories] = useState(FALLBACK_STORIES);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await fetchTales();
        const list = Array.isArray(data) ? data : data?.tales || data?.data || [];
        if (alive && list.length) {
          setStories(
            list.slice(0, 3).map((t) => ({
              _id: t._id || t.id,
              title: t.title,
              groupName: t.groupName || t.group || 'Story',
            }))
          );
        }
      } catch {
        /* keep fallbacks */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Rejection Tales',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Web, iOS',
    description:
      'A soft landing after rejection: share stories, sit with it, talk to Luna, and practice when you are ready.',
    url: 'https://rejectiontales.com/',
    downloadUrl: APP_STORE_URL,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: C.bg,
        color: C.text,
        ...sans,
        overflowX: 'hidden',
        '@keyframes rtIn': {
          from: { opacity: 0, transform: 'translateY(22px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes rtRise': {
          '0%, 100%': { transform: 'translateY(0) rotate(var(--rt-rot, 0deg))' },
          '50%': { transform: 'translateY(-12px) rotate(var(--rt-rot, 0deg))' },
        },
        '@keyframes rtSoft': {
          '0%, 100%': { opacity: 0.45, transform: 'scale(1)' },
          '50%': { opacity: 0.85, transform: 'scale(1.06)' },
        },
        '@keyframes rtSheen': {
          '0%': { backgroundPosition: '0% 50%' },
          '100%': { backgroundPosition: '100% 50%' },
        },
      }}
    >
      <Seo
        title="Rejection Tales"
        description="You got the no. You don’t have to carry it alone. Share stories, sit with it, talk to Luna, and practice when you’re ready."
        path={pathname === '/about' ? '/about' : '/'}
        schema={schema}
      />

      {/* ═══════════════ HERO ═══════════════ */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: { xs: 'auto', md: 'calc(100dvh - 64px)' },
          overflow: 'hidden',
          background: `
            radial-gradient(ellipse 70% 55% at 85% 15%, rgba(139,111,92,0.38) 0%, transparent 60%),
            radial-gradient(ellipse 50% 45% at 5% 85%, rgba(74,52,40,0.16) 0%, transparent 50%),
            radial-gradient(ellipse 40% 30% at 45% 100%, rgba(196,160,120,0.22) 0%, transparent 55%),
            linear-gradient(165deg, #F8F5F1 0%, ${C.bg} 42%, #E8E1D8 100%)
          `,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.035\'/%3E%3C/svg%3E")',
            pointerEvents: 'none',
          },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1,
            px: { xs: 2.5, sm: 4, md: 5 },
            py: { xs: 5, sm: 6, md: 4 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.08fr 0.92fr' },
            gap: { xs: 4.5, md: 4, lg: 6 },
            alignItems: 'center',
          }}
        >
          <Box sx={{ maxWidth: 560 }}>
            <Typography
              component="p"
              sx={{
                ...display,
                fontWeight: 700,
                fontSize: { xs: 'clamp(2.75rem, 9vw, 4rem)', md: 'clamp(3.4rem, 5vw, 4.75rem)' },
                lineHeight: 0.92,
                color: C.primary,
                mb: { xs: 2, md: 2.25 },
                background: `linear-gradient(120deg, ${C.primary} 30%, ${C.secondary} 55%, ${C.primary} 80%)`,
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'rtIn 0.8s ease-out both, rtSheen 8s ease-in-out infinite alternate',
              }}
            >
              Rejection
              <Box component="span" sx={{ display: 'block' }}>
                Tales
              </Box>
            </Typography>

            <Typography
              component="h1"
              sx={{
                ...display,
                fontWeight: 500,
                fontSize: { xs: '1.55rem', sm: '1.85rem', md: '2.05rem' },
                lineHeight: 1.28,
                color: C.text,
                mb: 1.75,
                maxWidth: 440,
                animation: 'rtIn 0.85s ease-out 0.08s both',
              }}
            >
              You got the “no.”
              <Box component="span" sx={{ display: 'block', color: C.primary, fontWeight: 700 }}>
                You don’t have to carry it alone.
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1.05rem', md: '1.12rem' },
                lineHeight: 1.55,
                color: C.muted,
                mb: 3.25,
                maxWidth: 400,
                animation: 'rtIn 0.85s ease-out 0.14s both',
              }}
            >
              Soft landing, real stories, Luna to talk to, and practice when you’re ready.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              flexWrap="wrap"
              useFlexGap
              sx={{
                animation: 'rtIn 0.85s ease-out 0.2s both',
                mb: 1.75,
              }}
            >
              <Button
                component={RouterLink}
                to="/tales"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: C.primary,
                  color: '#fff',
                  fontWeight: 700,
                  px: 3.75,
                  py: 1.6,
                  borderRadius: '999px',
                  fontSize: '1.05rem',
                  boxShadow: '0 10px 28px rgba(74,52,40,0.28)',
                  '&:hover': {
                    bgcolor: C.primaryDark,
                    boxShadow: '0 14px 32px rgba(74,52,40,0.34)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Read stories
              </Button>
              <AppStoreBadge tone="dark" />
            </Stack>

            <Typography
              component={RouterLink}
              to="/auth"
              sx={{
                display: 'inline-block',
                color: C.primary,
                fontWeight: 600,
                fontSize: '0.98rem',
                textDecoration: 'underline',
                textUnderlineOffset: 4,
                animation: 'rtIn 0.85s ease-out 0.28s both',
                '&:hover': { textDecorationThickness: 2 },
              }}
            >
              Or sign in on the web
            </Typography>
          </Box>

          {/* Product visual: phone with live story titles */}
          <Box
            aria-hidden
            sx={{
              position: 'relative',
              display: { xs: 'none', sm: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: { sm: 380, md: 460 },
              animation: 'rtIn 1s ease-out 0.16s both',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: { sm: 280, md: 340 },
                height: { sm: 280, md: 340 },
                borderRadius: '50%',
                background: `radial-gradient(circle at 40% 35%, rgba(255,255,255,0.9), ${C.secondary}44 45%, transparent 70%)`,
                animation: 'rtSoft 7s ease-in-out infinite',
                filter: 'blur(2px)',
              }}
            />

            {/* Floating slip behind phone */}
            <Box
              sx={{
                '--rt-rot': '-8deg',
                position: 'absolute',
                left: { sm: '2%', md: '0%' },
                top: { sm: '12%', md: '10%' },
                width: { sm: 180, md: 210 },
                p: 2,
                bgcolor: 'rgba(255,255,255,0.72)',
                border: `1px solid ${C.border}`,
                borderRadius: '14px',
                boxShadow: '0 18px 40px rgba(42,34,28,0.10)',
                transform: 'rotate(-8deg)',
                animation: 'rtRise 10s ease-in-out infinite',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Typography sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted, mb: 0.75 }}>
                {stories[1]?.groupName || 'Story'}
              </Typography>
              <Typography sx={{ ...display, fontSize: { sm: '0.92rem', md: '1rem' }, fontWeight: 600, lineHeight: 1.35, color: C.text }}>
                {(stories[1]?.title || '').slice(0, 72)}
                {(stories[1]?.title || '').length > 72 ? '…' : ''}
              </Typography>
            </Box>

            {/* Phone frame */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                width: { sm: 240, md: 280 },
                borderRadius: '36px',
                bgcolor: C.primaryDark,
                p: '10px',
                boxShadow: '0 30px 60px rgba(42,34,28,0.28), 0 0 0 1px rgba(255,255,255,0.08) inset',
                animation: 'rtRise 11s ease-in-out 0.5s infinite',
                '--rt-rot': '0deg',
              }}
            >
              <Box
                sx={{
                  bgcolor: C.bgAlt,
                  borderRadius: '28px',
                  overflow: 'hidden',
                  minHeight: { sm: 420, md: 480 },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ px: 2, pt: 1.75, pb: 1.25, borderBottom: `1px solid ${C.border}` }}>
                  <Box sx={{ width: 72, height: 5, borderRadius: 3, bgcolor: C.border, mx: 'auto', mb: 1.5 }} />
                  <Typography sx={{ ...display, fontWeight: 700, fontSize: '1.05rem', color: C.primary }}>
                    Stories
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: C.muted, mt: 0.25 }}>
                    Soft landings after a no
                  </Typography>
                </Box>
                <Stack spacing={1.25} sx={{ p: 1.5, flex: 1 }}>
                  {stories.slice(0, 3).map((s, i) => (
                    <Box
                      key={s._id || i}
                      sx={{
                        bgcolor: C.surface,
                        borderRadius: '14px',
                        border: `1px solid ${C.border}`,
                        borderLeft: `3px solid ${i === 0 ? C.primary : C.secondary}`,
                        p: 1.5,
                        boxShadow: i === 0 ? '0 8px 20px rgba(42,34,28,0.08)' : 'none',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          letterSpacing: '0.07em',
                          textTransform: 'uppercase',
                          color: C.muted,
                          mb: 0.5,
                        }}
                      >
                        {s.groupName}
                      </Typography>
                      <Typography
                        sx={{
                          ...display,
                          fontSize: '0.84rem',
                          fontWeight: 600,
                          lineHeight: 1.35,
                          color: C.text,
                        }}
                      >
                        {(s.title || '').slice(0, 68)}
                        {(s.title || '').length > 68 ? '…' : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>

            {/* Soft orb accent */}
            <Box
              sx={{
                position: 'absolute',
                width: 100,
                height: 100,
                borderRadius: '50%',
                right: { sm: '4%', md: '2%' },
                bottom: '8%',
                background: `radial-gradient(circle at 40% 35%, #fff, ${C.secondary}66)`,
                animation: 'rtSoft 6.5s ease-in-out 1s infinite',
                filter: 'blur(1px)',
                zIndex: 1,
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* ═══════════════ PILLARS ═══════════════ */}
      <Box
        component="section"
        sx={{
          py: { xs: 7, md: 10 },
          bgcolor: C.surface,
          borderTop: `1px solid ${C.border}`,
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 4, md: 5 } }}>
          <Typography
            component="h2"
            sx={{
              ...display,
              fontWeight: 600,
              fontSize: { xs: '1.75rem', md: '2.15rem' },
              mb: 1,
            }}
          >
            What you get
          </Typography>
          <Typography sx={{ color: C.muted, mb: { xs: 4, md: 5 }, maxWidth: 420, lineHeight: 1.6 }}>
            One calm place for the moment after a no, and the practice that comes next.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
              gap: 0,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            {PILLARS.map((p, i) => (
              <Box
                key={p.title}
                sx={{
                  py: { xs: 3, md: 4 },
                  borderBottom: { xs: `1px solid ${C.border}`, lg: 'none' },
                  borderLeft: {
                    xs: 'none',
                    sm: i % 2 === 1 ? `1px solid ${C.border}` : 'none',
                    lg: i === 0 ? 'none' : `1px solid ${C.border}`,
                  },
                  pl: {
                    xs: 0,
                    sm: i % 2 === 1 ? 3 : 0,
                    lg: i === 0 ? 0 : 3.5,
                  },
                  pr: { lg: 3 },
                }}
              >
                <Typography
                  sx={{
                    ...display,
                    fontWeight: 600,
                    fontSize: '1.35rem',
                    color: C.primary,
                    mb: 1,
                  }}
                >
                  {p.title}
                </Typography>
                <Typography sx={{ color: C.muted, lineHeight: 1.65, fontSize: '0.98rem', pr: 1 }}>
                  {p.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════ PATH ═══════════════ */}
      <Box
        component="section"
        sx={{
          py: { xs: 7, md: 10 },
          background: `linear-gradient(180deg, ${C.bgAlt}, ${C.bg})`,
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2.5, sm: 4 } }}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: C.secondary,
              fontWeight: 600,
              mb: 2,
            }}
          >
            A quieter sequence
          </Typography>
          <Typography
            component="h2"
            sx={{
              ...display,
              fontWeight: 600,
              fontSize: { xs: '1.75rem', md: '2.15rem' },
              mb: { xs: 4, md: 5 },
            }}
          >
            From the sting to the next step
          </Typography>

          <Stack>
            {PATH.map((step, i) => (
              <Box
                key={step.n}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '48px 1fr', sm: '72px 1fr' },
                  gap: { xs: 1.5, sm: 3 },
                  py: { xs: 2.5, md: 3 },
                  borderTop: i === 0 ? `1px solid ${C.border}` : 'none',
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <Typography
                  sx={{
                    ...display,
                    fontSize: '1rem',
                    color: C.secondary,
                    fontWeight: 500,
                  }}
                >
                  {step.n}
                </Typography>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '1.15rem',
                      color: C.primary,
                      mb: 0.4,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography sx={{ color: C.muted, lineHeight: 1.6 }}>{step.body}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ═══════════════ LIVE STORIES ═══════════════ */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 }, bgcolor: C.surface }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 4, md: 5 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'flex-end' }}
            spacing={2}
            sx={{ mb: { xs: 4, md: 5 } }}
          >
            <Box>
              <Typography
                component="h2"
                sx={{
                  ...display,
                  fontWeight: 600,
                  fontSize: { xs: '1.75rem', md: '2.15rem' },
                  mb: 1,
                }}
              >
                Stories people carried
              </Typography>
              <Typography sx={{ color: C.muted, maxWidth: 380, lineHeight: 1.55 }}>
                Open any title. Guests can read. Members can share and leave empathy.
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/tales"
              sx={{
                color: C.primary,
                fontWeight: 600,
                alignSelf: { xs: 'flex-start', sm: 'auto' },
                textTransform: 'none',
                px: 0,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              See all stories →
            </Button>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 2, md: 2.5 },
            }}
          >
            {stories.map((s) => (
              <Box
                key={s._id}
                component={RouterLink}
                to={s._id?.startsWith('fallback') ? '/tales' : `/tales/${s._id}`}
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  p: { xs: 2.5, md: 3 },
                  bgcolor: C.bgAlt,
                  borderRadius: 2,
                  border: `1px solid ${C.border}`,
                  borderLeft: `3px solid ${C.primary}`,
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 16px 40px rgba(42,34,28,0.08)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: C.secondary,
                    fontWeight: 600,
                    mb: 1.25,
                  }}
                >
                  {s.groupName}
                </Typography>
                <Typography
                  sx={{
                    ...display,
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', md: '1.15rem' },
                    lineHeight: 1.35,
                    color: C.text,
                  }}
                >
                  {s.title}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════ LINE ═══════════════ */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 11 },
          background: `linear-gradient(165deg, #E8E4DF 0%, ${C.bg} 100%)`,
        }}
      >
        <Container maxWidth="sm" sx={{ px: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
          <Typography
            sx={{
              ...display,
              fontWeight: 500,
              fontStyle: 'italic',
              fontSize: { xs: '1.45rem', md: '1.75rem' },
              lineHeight: 1.4,
              color: C.text,
              mb: 2.5,
            }}
          >
            LinkedIn celebrates the yes. Almost no one sits with the no.
          </Typography>
          <Typography sx={{ color: C.muted, fontSize: '1.05rem', lineHeight: 1.6 }}>
            Rejection Tales is the pause between the sting and the next attempt.
          </Typography>
        </Container>
      </Box>

      {/* ═══════════════ CTA ═══════════════ */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 11 },
          background: `
            radial-gradient(ellipse 70% 80% at 90% 20%, rgba(139,111,92,0.4) 0%, transparent 50%),
            linear-gradient(160deg, ${C.primary} 0%, ${C.primaryDark} 100%)
          `,
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2.5, sm: 4 } }}>
          <Typography
            component="h2"
            sx={{
              ...display,
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.6rem' },
              color: '#F7F6F4',
              mb: 2,
              maxWidth: 400,
              lineHeight: 1.15,
            }}
          >
            Begin with one story
          </Typography>
          <Typography
            sx={{
              color: 'rgba(247,246,244,0.76)',
              fontSize: '1.1rem',
              mb: 4,
              maxWidth: 400,
              lineHeight: 1.6,
            }}
          >
            Read freely. Sign in to share yours, talk with Luna, and practice on web or iOS.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap="wrap"
            useFlexGap
            sx={{ '& > a.MuiButton-root': { width: { xs: '100%', sm: 'auto' } } }}
          >
            <Button
              component={RouterLink}
              to="/tales"
              size="large"
              sx={{
                bgcolor: '#F7F6F4',
                color: C.primary,
                fontWeight: 600,
                px: 3.5,
                py: 1.5,
                borderRadius: '999px',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#fff', boxShadow: 'none' },
              }}
            >
              Read stories
            </Button>
            <Button
              component={RouterLink}
              to="/auth"
              size="large"
              variant="outlined"
              sx={{
                borderColor: 'rgba(247,246,244,0.35)',
                color: '#F7F6F4',
                fontWeight: 600,
                px: 3.5,
                py: 1.5,
                borderRadius: '999px',
                '&:hover': {
                  borderColor: '#F7F6F4',
                  bgcolor: 'rgba(247,246,244,0.08)',
                },
              }}
            >
              Sign in
            </Button>
            <AppStoreBadge tone="light" />
          </Stack>
        </Container>
      </Box>

      <Box
        component="aside"
        sx={{ py: 2.5, bgcolor: C.bgAlt, borderTop: `1px solid ${C.border}` }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 4, md: 5 } }}>
          <Typography sx={{ fontSize: '0.8rem', color: C.muted, lineHeight: 1.5 }}>
            Rejection Tales is not a crisis service. If you are in immediate danger, call 911 or
            your local emergency line.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;
